import * as React from 'react';

import { Code2 } from 'lucide-react';

import { cn } from '../../lib/utils';

const AVATAR_FALLBACK = [
  'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=96&q=80',
  'https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=96&q=80',
  'https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=96&q=80',
];

function parseAuthor(authorLine) {
  if (!authorLine || typeof authorLine !== 'string')
    return { name: '', profession: '' };
  const sep = authorLine.includes(' — ') ? ' — ' : ' - ';
  const idx = authorLine.indexOf(sep);
  if (idx === -1) return { name: authorLine.trim(), profession: '' };
  return {
    name: authorLine.slice(0, idx).trim(),
    profession: authorLine.slice(idx + sep.length).trim(),
  };
}

function mapTestimonials(rows) {
  return rows.map((row, i) => {
    const { name, profession } = parseAuthor(row.author);
    const description = row.testimonial ?? row.description ?? '';
    const avatar =
      row.avatar ??
      row.avatarUrl ??
      AVATAR_FALLBACK[i % AVATAR_FALLBACK.length];
    return {
      id: String(row.id ?? i),
      name,
      profession,
      description,
      avatar,
    };
  });
}

/**
 * Infinite horizontal marquee of testimonial cards (CRA + Tailwind; no Next.js Image).
 */
export function SlidingTestimonials({
  title,
  subtitle,
  testimonials = [],
  className,
}) {
  const items = React.useMemo(
    () => mapTestimonials(testimonials),
    [testimonials],
  );
  const duplicated = React.useMemo(() => [...items, ...items], [items]);

  if (items.length === 0) return null;

  const fadeMask =
    'linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)';

  return (
    <section
      className={cn(
        'w-full max-w-[100vw] overflow-hidden border-y border-white/10 bg-neutral-950 py-16',
        className,
      )}
    >
      <div className="mx-auto w-full max-w-7xl px-4 md:px-10">
        <div className="mb-10 text-center">
          <h2 className="mx-auto mt-6 max-w-3xl font-sans text-3xl font-normal tracking-tight text-white md:text-4xl lg:text-5xl">
            {title}
          </h2>
          {subtitle ? (
            <p className="mx-auto mt-4 max-w-xl font-sans text-base text-neutral-300 md:text-lg">
              {subtitle}
            </p>
          ) : null}
        </div>

        <div
          className="relative max-w-full shrink-0 overflow-hidden"
          style={{
            maskImage: fadeMask,
            WebkitMaskImage: fadeMask,
          }}
        >
          <div className="flex w-max animate-x-slider gap-5 motion-reduce:animate-none">
            {duplicated.map((entry, idx) => (
              <article
                key={`${entry.id}-${idx}`}
                className="flex h-full w-[min(560px,calc(100vw-3rem))] shrink-0 grow-0 flex-col rounded-xl border border-white/15 bg-neutral-900/90 shadow-lg backdrop-blur-sm sm:w-[600px]"
              >
                <p className="px-5 py-6 text-pretty text-lg font-extralight leading-snug tracking-tight text-neutral-100 sm:text-xl md:text-2xl">
                  &quot;{entry.description}&quot;
                </p>
                <div className="flex w-full gap-0 overflow-hidden border-t border-white/10">
                  <div className="flex w-[72%] items-center gap-3 px-4 py-3 md:w-3/4">
                    <img
                      src={entry.avatar}
                      alt=""
                      width={48}
                      height={48}
                      loading="lazy"
                      decoding="async"
                      referrerPolicy="no-referrer"
                      className="size-11 shrink-0 rounded-full object-cover ring-2 ring-white/10"
                    />
                    <div className="flex min-w-0 flex-col items-start">
                      <h3 className="text-base font-medium text-white md:text-lg">
                        {entry.name}
                      </h3>
                      <p className="text-sm text-sky-200/95 md:text-base">
                        {entry.profession}
                      </p>
                    </div>
                  </div>
                  <div className="w-px shrink-0 bg-white/15" aria-hidden />
                  <div className="flex min-w-0 flex-1 items-center justify-start self-stretch pl-3">
                    <div className="flex items-center gap-2 px-2 text-sky-200/85">
                      <Code2
                        className="size-7 shrink-0 text-[#47b0ff]"
                        aria-hidden
                      />
                      <span className="text-xs font-semibold uppercase tracking-[0.2em]">
                        Futuro<span className="text-white">Code</span>
                      </span>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default SlidingTestimonials;
